/**
 * Оверлей тура (E4.6b, макет training-dialog/training-panel): подсветка цели шага и
 * панель с текстом, прогрессом и действиями. Radix Dialog удерживает фокус в панели и
 * возвращает его; Escape и крестик закрывают тур, «Вернуться к работе» приостанавливает с возвратом в
 * точку начала. На узком или низком экране панель по центру без подсветки. Незавершённые
 * изменения страницы не подтверждаются за пользователя: тур ждёт его решения.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { discardDirtyDrafts, hasDirtyDrafts, saveDirtyDrafts } from '@/stores/workspaceDrafts';
import { LESSONS } from './catalog';
import { hintKey } from './roleHints';
import { findTrainingTarget } from './targets';
import { sameHref, tourStepHref } from './tourRoutes';
import { leaveTour, useTourStore } from './tourStore';
import type { TourPhase } from './tourMachine';
import { useTrainingExercise } from './useTrainingExercise';
import { useTrainingProgress } from './useTrainingProgress';

const VISIBLE: readonly TourPhase[] = [
	'welcome',
	'preparing',
	'showing',
	'unavailable',
	'blocked',
	'finished',
];

/** Экран, на котором панель встаёт рядом с целью (макет: шире 700 и выше 560) */
function roomy(): boolean {
	return window.innerWidth > 700 && window.innerHeight > 560;
}

interface Placement {
	spot: CSSProperties | null;
	panel: CSSProperties | undefined;
	moving?: boolean;
}

const CENTERED: Placement = { spot: null, panel: undefined };
const GAP = 16;
const PANEL_WIDTH = 390;

/** Размещение панели рядом с подсвеченной целью; без места -- по центру */
function place(target: HTMLElement | null, panel: HTMLElement | null): Placement {
	if (!target || !panel || !roomy()) return CENTERED;
	const box = target.getBoundingClientRect();
	const rect = {
		left: Math.max(8, box.left - 5),
		top: Math.max(8, box.top - 5),
		right: Math.min(window.innerWidth - 8, box.right + 5),
		bottom: Math.min(window.innerHeight - 8, box.bottom + 5),
	};
	const width = Math.min(PANEL_WIDTH, window.innerWidth - 2 * GAP);
	const height = panel.getBoundingClientRect().height;
	let left = Math.max(GAP, Math.min(window.innerWidth - width - GAP, rect.left));
	let top: number;
	if (rect.right + GAP + width <= window.innerWidth - GAP) {
		left = rect.right + GAP;
		top = Math.max(GAP, Math.min(rect.top, window.innerHeight - height - GAP));
	} else if (rect.left - width - GAP >= GAP) {
		left = rect.left - width - GAP;
		top = Math.max(GAP, Math.min(rect.top, window.innerHeight - height - GAP));
	} else if (rect.bottom + GAP + height <= window.innerHeight - GAP) {
		top = rect.bottom + GAP;
	} else if (rect.top - height - GAP >= GAP) {
		top = rect.top - height - GAP;
	} else {
		top = window.innerHeight - height - GAP;
		rect.bottom = Math.min(rect.bottom, top - GAP);
	}
	if (rect.bottom - rect.top < 28 || rect.right <= rect.left) return CENTERED;
	return {
		spot: {
			left: rect.left,
			top: rect.top,
			width: rect.right - rect.left,
			height: rect.bottom - rect.top,
		},
		panel: { left, top, transform: 'none', width },
	};
}

export function TrainingOverlay() {
	const { t } = useTranslation('learning');
	const { tour: currentTour, manifest, dispatch } = useTourStore();
	const open = VISIBLE.includes(currentTour.phase) && currentTour.lesson !== null;
	// Последний кадр остаётся на время закрытия, но не переживает сброс учебной сессии.
	const [tour, setTour] = useState(currentTour);
	if (open && tour !== currentTour) setTour(currentTour);
	const { exitTraining } = useWorkspaceDataSource();
	const { start: startExercise } = useTrainingExercise();
	const { progress, warning } = useTrainingProgress();
	const isAdmin = useIsAdmin();
	const [panel, setPanel] = useState<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [placement, setPlacement] = useState<Placement>(CENTERED);
	const [saveFailed, setSaveFailed] = useState(false);
	const lesson = tour.lesson === null ? null : LESSONS[tour.lesson];
	const step = lesson === null ? null : lesson.steps[tour.index];

	/* Подсветка следует за целью: прокрутка, изменение размеров окна и самой цели */
	useLayoutEffect(() => {
		if (!open || tour.phase !== 'showing' || step === null || !panel?.isConnected) return undefined;
		const target = findTrainingTarget(step);
		target?.scrollIntoView({ block: 'center', behavior: 'instant' });
		const update = (moving = false) => {
			const next = place(findTrainingTarget(step), panel);
			setPlacement((current) =>
				JSON.stringify(current.spot) === JSON.stringify(next.spot) &&
				JSON.stringify(current.panel) === JSON.stringify(next.panel)
					? current
					: { ...next, moving },
			);
		};
		/* Новый шаг движется от прежней позиции. Прокрутка и resize следуют за целью сразу. */
		const frame = requestAnimationFrame(() => update(true));
		const followTarget = () => update();
		// Первое уведомление observer может прийти раньше кадра первоначального размещения.
		const observer = new ResizeObserver(() => update(true));
		if (target) observer.observe(target);
		if (panel) observer.observe(panel);
		window.addEventListener('resize', followTarget);
		window.addEventListener('scroll', followTarget, true);
		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
			window.removeEventListener('resize', followTarget);
			window.removeEventListener('scroll', followTarget, true);
		};
	}, [panel, open, step, tour.index, tour.phase]);
	/* Подсветка -- только у показанного шага */
	const shownPlacement =
		tour.phase === 'showing'
			? placement
			: tour.phase === 'preparing'
				? { ...CENTERED, panel: placement.panel }
				: CENTERED;

	if (lesson === null || tour.lesson === null || manifest === null) return null;
	const lessonId = tour.lesson;

	/** Переход меняет адрес, а на странице незавершённые изменения */
	const dirtyTo = (index: number) =>
		manifest !== null &&
		hasDirtyDrafts() &&
		!sameHref(
			`${window.location.pathname}${window.location.search}`,
			tourStepHref(lessonId, index, manifest),
		);

	const next = () =>
		dispatch({
			type: 'next',
			dirty: tour.index + 1 < lesson.steps.length && dirtyTo(tour.index + 1),
		});
	const skip = () =>
		dispatch({
			type: 'skip',
			dirty: tour.index + 1 < lesson.steps.length && dirtyTo(tour.index + 1),
		});
	const back = () => dispatch({ type: 'back', dirty: dirtyTo(tour.index - 1) });
	const pause = () => leaveTour('pause', true);
	/* Пройденный урок не ставится на паузу: закрытие финала завершает тур */
	const dismiss = () => leaveTour('close', true);
	const toCatalog = () => {
		leaveTour('close', false);
		exitTraining('/learning');
	};
	const explore = () => leaveTour('close', false);
	const restart = () => dispatch({ type: 'restart', dirty: dirtyTo(0) });

	const saveDrafts = async () => {
		setSaveFailed(false);
		if ((await saveDirtyDrafts()) && !hasDirtyDrafts()) dispatch({ type: 'draftsResolved' });
		else setSaveFailed(true);
	};
	const discardDrafts = () => {
		discardDirtyDrafts();
		if (!hasDirtyDrafts()) dispatch({ type: 'draftsResolved' });
	};

	const close = (
		<DialogPrimitive.Close
			className="ws-icon-button ws-training-close"
			aria-label={t('tour.close')}
		>
			<WorkspaceIcon name="close" />
		</DialogPrimitive.Close>
	);
	const completed = progress.lessons[lessonId]?.status === 'completed';

	let body;
	if (tour.phase === 'welcome') {
		body = (
			<>
				{close}
				<span className="ws-learning-icon">
					<WorkspaceIcon name="overview" size={26} />
				</span>
				<span className="ws-training-kicker">{t('tour.welcome.kicker')}</span>
				<DialogPrimitive.Title>{t('tour.welcome.title')}</DialogPrimitive.Title>
				<DialogPrimitive.Description>{t('tour.welcome.text')}</DialogPrimitive.Description>
				<ol className="ws-training-welcome-steps list-decimal">
					<li>{t('tour.welcome.step1')}</li>
					<li>{t('tour.welcome.step2')}</li>
					<li>{t('tour.welcome.step3')}</li>
				</ol>
				<div className="ws-training-panel-actions">
					<button
						type="button"
						className="ws-btn ws-primary"
						onClick={() => dispatch({ type: 'begin' })}
						data-training-primary=""
					>
						{t('tour.welcome.begin')}
						<WorkspaceIcon name="arrow" size={16} />
					</button>
					<button type="button" className="ws-btn" onClick={dismiss}>
						{t('tour.later')}
					</button>
				</div>
				<small>{t('tour.welcome.meta')}</small>
			</>
		);
	} else if (tour.phase === 'finished') {
		const exercise = lesson.exercise;
		body = (
			<>
				{close}
				<span className="ws-learning-icon">
					<WorkspaceIcon name={completed ? 'check' : 'report'} size={26} />
				</span>
				<span className="ws-training-kicker">
					{completed ? t('tour.finish.doneKicker') : t('tour.finish.pausedKicker')}
				</span>
				<DialogPrimitive.Title>
					{completed ? t('tour.finish.doneTitle') : t('tour.finish.pausedTitle')}
				</DialogPrimitive.Title>
				<DialogPrimitive.Description>
					{completed ? t(`learning:lessons.${lessonId}.outcome`) : t('tour.finish.pausedText')}
				</DialogPrimitive.Description>
				<p className="ws-training-finish-note">{t('tour.finish.note')}</p>
				<div className="ws-training-panel-actions">
					{exercise ? (
						<button
							type="button"
							className="ws-btn ws-primary"
							data-training-primary=""
							onClick={() => void startExercise(exercise)}
						>
							{t('tour.finish.exercise')}
							<WorkspaceIcon name="arrow" size={16} />
						</button>
					) : (
						<button
							type="button"
							className="ws-btn ws-primary"
							data-training-primary=""
							onClick={explore}
						>
							{t('tour.finish.explore')}
							<WorkspaceIcon name="arrow" size={16} />
						</button>
					)}
					<button type="button" className="ws-btn" onClick={toCatalog}>
						{t('tour.finish.catalog')}
					</button>
				</div>
				{!completed && (
					<button type="button" className="ws-text-link" onClick={restart}>
						{t('tour.finish.restart')}
					</button>
				)}
				<small>{warning === null ? t('tour.finish.saved') : t('tour.finish.notSaved')}</small>
			</>
		);
	} else if (tour.phase === 'blocked') {
		body = (
			<>
				{close}
				<span className="ws-training-kicker">{t(`learning:lessons.${lessonId}.short`)}</span>
				<DialogPrimitive.Title>{t('tour.blocked.title')}</DialogPrimitive.Title>
				<DialogPrimitive.Description>{t('tour.blocked.text')}</DialogPrimitive.Description>
				{saveFailed && (
					<p className="ws-target-form-error" role="alert">
						{t('tour.blocked.saveFailed')}
					</p>
				)}
				<div className="ws-training-panel-actions">
					<button
						type="button"
						className="ws-btn"
						onClick={() => dispatch({ type: 'continue' })}
						data-training-primary=""
					>
						{t('tour.blocked.continue')}
					</button>
					<button type="button" className="ws-btn" onClick={discardDrafts}>
						{t('tour.blocked.discard')}
					</button>
					<button type="button" className="ws-btn ws-primary" onClick={() => void saveDrafts()}>
						{t('tour.blocked.save')}
					</button>
				</div>
			</>
		);
	} else if (step !== null) {
		const unavailable = tour.phase === 'unavailable';
		/* Страница шага закрыта для роли: повтор ничего не изменит, остаётся пропуск */
		const closedPage = unavailable && tour.unavailable === 'page';
		const preparing = tour.phase === 'preparing';
		const last = tour.index === lesson.steps.length - 1;
		body = (
			<>
				{close}
				<div className="ws-training-step-meta">
					<span>{t(`learning:lessons.${lessonId}.short`)}</span>
					<span aria-live="polite">
						{t('tour.step', { current: tour.index + 1, total: lesson.steps.length })}
					</span>
				</div>
				<div className="ws-training-step-bars" aria-hidden="true">
					{lesson.steps.map((id, index) => (
						<i key={`${id}-${index}`} className={index <= tour.index ? 'ws-active' : undefined} />
					))}
				</div>
				<DialogPrimitive.Title>
					{unavailable ? t('tour.unavailable.title') : t(`learning:steps.${step}.title`)}
				</DialogPrimitive.Title>
				<DialogPrimitive.Description>
					{unavailable
						? t(closedPage ? 'tour.unavailable.pageText' : 'tour.unavailable.text')
						: preparing
							? t('tour.preparing')
							: t(`learning:steps.${step}.body`)}
				</DialogPrimitive.Description>
				{!preparing && (
					<div className="ws-training-tip">
						<WorkspaceIcon name="report" size={15} />
						<span>{t(hintKey(`steps.${step}`, isAdmin))}</span>
					</div>
				)}
				{lessonId === 'demo' && (
					<label className="ws-training-jump">
						{t('tour.jump')}
						<select
							value={tour.index}
							onChange={(event) => {
								const index = Number(event.target.value);
								dispatch({ type: 'jump', index, dirty: dirtyTo(index) });
							}}
						>
							{lesson.steps.map((id, index) => (
								<option key={`${id}-${index}`} value={index}>
									{`${index + 1}. ${t(`learning:steps.${id}.title`)}`}
								</option>
							))}
						</select>
					</label>
				)}
				<div className="ws-training-panel-actions">
					<button type="button" className="ws-btn" onClick={back} disabled={tour.index === 0}>
						{t('tour.back')}
					</button>
					{unavailable ? (
						<>
							{!closedPage && (
								<button
									type="button"
									className="ws-btn"
									onClick={() => dispatch({ type: 'retry' })}
								>
									{t('tour.retry')}
								</button>
							)}
							<button
								type="button"
								className="ws-btn ws-primary"
								onClick={next}
								data-training-primary=""
							>
								{t('tour.skip')}
							</button>
						</>
					) : (
						<>
							{preparing && (
								<button type="button" className="ws-btn" onClick={skip}>
									{t('tour.skip')}
								</button>
							)}
							<button
								type="button"
								className="ws-btn ws-primary"
								onClick={next}
								disabled={preparing}
								data-training-primary=""
							>
								{last ? t('tour.finishLesson') : t('tour.next')}
								<WorkspaceIcon name="arrow" size={16} />
							</button>
						</>
					)}
				</div>
				<button type="button" className="ws-training-return" onClick={pause}>
					{t('tour.return')}
				</button>
			</>
		);
	}

	return (
		/* Каждая фаза и шаг -- новый слой поверх открытых страницей панелей (карточка события,
		   быстрый просмотр ресурса): так панель тура остаётся верхней и доступной */
		<DialogPrimitive.Root
			key={`${tour.lesson}-${tour.phase}-${tour.index}`}
			open={open}
			onOpenChange={(next) => !next && dismiss()}
		>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Content
					ref={contentRef}
					data-moving={shownPlacement.moving || undefined}
					style={open ? undefined : { pointerEvents: 'none' }}
					className={
						shownPlacement.spot ? 'ws-training-dialog ws-has-spotlight' : 'ws-training-dialog'
					}
					onPointerDownOutside={() => dismiss()}
					onClick={(event) => {
						if (event.target instanceof Element && !event.target.closest('.ws-training-panel'))
							dismiss();
					}}
					onOpenAutoFocus={(event) => {
						/* Фокус -- на основное действие фазы, без прокрутки страницы */
						event.preventDefault();
						const scope = contentRef.current;
						const primary = scope?.querySelector<HTMLElement>(
							'[data-training-primary]:not(:disabled)',
						);
						(primary ?? scope?.querySelector<HTMLElement>('button'))?.focus({
							preventScroll: true,
						});
					}}
					/* Фокус после тура возвращает исполнитель в точку начала, смена фазы его не трогает */
					onCloseAutoFocus={(event) => event.preventDefault()}
				>
					{shownPlacement.spot && (
						<div className="ws-training-spotlight" style={shownPlacement.spot} aria-hidden="true" />
					)}
					<div ref={setPanel} className="ws-training-panel" style={shownPlacement.panel}>
						{body}
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
